import type { Project } from '../data/types';
import { Button } from '../components/Button/Button';
import './ProjectCard.css';

const KIND_LABEL: Record<Project['kind'], string> = {
  image: 'Camera',
  audio: 'Mic',
  pose: 'Pose',
};

const KIND_GLYPH: Record<Project['kind'], string> = {
  image: '◉',
  audio: '◌',
  pose: '✦',
};

export interface ProjectCardProps {
  project: Project;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  /** When the kid has published this project to bryta.app, the
   *  Home view passes the version + share URL so we can render a
   *  small "Published" chip — same project, kid sees the status
   *  before clicking in. */
  publishedVersion?: number;
  publishedUrl?: string;
}

/** Replace ASCII hyphen-minus with the non-breaking hyphen (U+2011)
 *  so display tokens like "wave-detector" wrap as a unit instead of
 *  splitting at the dash. The aria-label keeps the original for
 *  screen readers and the kid's stored name is untouched. */
function nonBreakingHyphens(s: string): string {
  return s.replace(/-/g, '‑');
}

export function ProjectCard({
  project,
  onOpen,
  onDelete,
  publishedVersion,
  publishedUrl,
}: ProjectCardProps): JSX.Element {
  return (
    <article className="bl-card" data-testid={`project-card-${project.id}`}>
      <button
        type="button"
        className={`bl-card__open${
          publishedVersion !== undefined ? ' bl-card__open--published' : ''
        }`}
        onClick={() => onOpen(project.id)}
        aria-label={`Open project ${project.name}`}
      >
        <span className="bl-card__glyph" aria-hidden="true">
          {KIND_GLYPH[project.kind]}
        </span>
        <span className="bl-card__name">{nonBreakingHyphens(project.name)}</span>
        <span className="bl-card__kind">
          {KIND_LABEL[project.kind]}
          {project.remixedAt !== undefined && (
            <span
              className="bl-card__remix"
              aria-label="Remixed from a gallery submission"
              title="Started as a remix of someone else's gallery submission."
            >
              remix
            </span>
          )}
        </span>
        {publishedVersion !== undefined && (
          <span
            className="bl-card__published"
            aria-label={`Published, version ${publishedVersion}`}
          >
            v{publishedVersion}
          </span>
        )}
      </button>
      {publishedUrl && (
        <a
          className="bl-card__view-live"
          href={publishedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label="View published page"
        >
          View live ↗
        </a>
      )}
      <Button
        variant="secondary"
        className="bl-card__delete"
        onClick={() => onDelete(project.id)}
        aria-label={`Delete project ${project.name}`}
      >
        Delete
      </Button>
    </article>
  );
}
